import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const JOIN_BONUS = 50000;
const PROMPTED_KEY = "pfp_join_prompted"; // per browser session: the prompt shows once each visit
const LOW_KEY = "pfp_low_chips_nudged"; // per browser session: the low-chips banner shows once each visit

const Field = ({ label, testId, ...p }) => (
  <label className="flex flex-col gap-1 text-xs text-slate-400">{label}
    <input data-testid={testId} {...p} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-base text-slate-100 focus:border-amber-400 outline-none" />
  </label>
);

// A guest becomes a member: same account, same chips (plus the bonus), same seat.
export const JoinDialog = ({ open, onOpenChange, reason }) => {
  const { join } = useAuth();
  const [f, setF] = useState({ display_name: "", dob: "", email: "", password: "", password2: "", referral_code: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password2) return setErr("The two passwords don't match");
    setErr(""); setBusy(true);
    try {
      const data = await join({ ...f, referral_code: f.referral_code.trim() || undefined });
      onOpenChange(false);
      // the guest-only buttons vanish now they're a member, so the always-mounted host plays the welcome
      window.dispatchEvent(new CustomEvent("pfp-joined", { detail: { name: data.user.display_name, chips: data.welcome?.join_chips || JOIN_BONUS } }));
    } catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gold-card border-amber-300/20 max-w-md max-h-[92vh] overflow-y-auto" data-testid="join-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-amber-50 tracking-wider">JOIN PLAYERS ROOM</DialogTitle>
          <DialogDescription className="text-slate-300">
            {reason || "Enjoying the game?"} Join free and get <span className="text-amber-300 font-semibold">{JOIN_BONUS.toLocaleString()} chips</span>. Every chip you have now comes with you.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-xs text-slate-400 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="join-perks">
          <li>· Every avatar</li><li>· Table colours &amp; card decks</li><li>· VIP points</li><li>· Club rooms</li>
          <li className="col-span-2">· Leaderboards &amp; monthly chip prizes</li>
          <li className="col-span-2">· Emails about upcoming tournaments &amp; events</li>
        </ul>
        <form onSubmit={submit} className="flex flex-col gap-3" data-testid="join-form">
          <Field label="Screen name" testId="join-display-name" required minLength={2} maxLength={24} value={f.display_name} onChange={set("display_name")} />
          <Field label="Date of birth · 18+ only" testId="join-dob" type="date" required max={new Date().toISOString().slice(0, 10)} value={f.dob} onChange={set("dob")} />
          <Field label="Email" testId="join-email" type="email" required value={f.email} onChange={set("email")} />
          <Field label="Password" testId="join-password" type="password" required minLength={8} value={f.password} onChange={set("password")} />
          <Field label="Confirm password" testId="join-password2" type="password" required minLength={8} value={f.password2} onChange={set("password2")} />
          <Field label="Referral code (optional)" testId="join-referral-code" maxLength={12} value={f.referral_code} onChange={set("referral_code")} />
          {err && <div data-testid="join-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
          <button data-testid="join-submit" disabled={busy} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift">
            {busy ? "…" : `JOIN & CLAIM ${JOIN_BONUS.toLocaleString()} CHIPS`}
          </button>
          <button type="button" data-testid="join-later" onClick={() => onOpenChange(false)} className="text-xs text-slate-400 hover:text-amber-300 underline underline-offset-4">Keep playing as a guest</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Button for anywhere a guest sees a locked feature.
export const JoinButton = ({ className = "", label = "JOIN", testId = "join-open-button", reason }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={testId} onClick={() => setOpen(true)} className={`btn-gold rounded-full px-3 py-1.5 text-xs font-display tracking-wider btn-lift ${className}`}>{label}</button>
      <JoinDialog open={open} onOpenChange={setOpen} reason={reason} />
    </>
  );
};

// After 25 hands, a guest is asked to join — once per visit. Running low on chips shows a gentle banner, also once per visit.
export const GuestJoinPrompt = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [low, setLow] = useState(false);
  const guest = !!user?.guest;
  useEffect(() => {
    if (!guest) return;
    let stop = false;
    const check = () => api.get("/api/auth/guest-status").then((r) => {
      if (stop) return;
      if (r.data.prompt && !sessionStorage.getItem(PROMPTED_KEY)) { sessionStorage.setItem(PROMPTED_KEY, "1"); setOpen(true); }
      else if (r.data.low_chips && !sessionStorage.getItem(LOW_KEY)) { sessionStorage.setItem(LOW_KEY, "1"); setLow(true); }
    }).catch(() => {});
    check();
    const id = setInterval(check, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [guest]);
  if (!guest) return null;
  return (
    <>
      {low && <LowChipsBanner onJoin={() => { setLow(false); setOpen(true); }} onClose={() => setLow(false)} />}
      <JoinDialog open={open} onOpenChange={setOpen} reason={low ? "Running low on chips?" : "You've played 25 hands as a guest."} />
    </>
  );
};

const LowChipsBanner = ({ onJoin, onClose }) => (
  <div data-testid="guest-low-chips-banner" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] gold-card rounded-2xl pl-5 pr-2 py-2.5 flex items-center gap-3 shadow-2xl low-chips-in max-w-[92vw]">
    <span className="text-sm text-amber-50">Running low on chips? <span className="text-slate-300">Join free and get</span> <span className="text-amber-300 font-semibold">50,000 more</span><span className="text-slate-300"> — plus leaderboards and monthly prizes</span>.</span>
    <button type="button" data-testid="guest-low-chips-join" onClick={onJoin} className="btn-gold rounded-full px-3 py-1.5 text-xs font-display tracking-wider btn-lift">JOIN</button>
    <button type="button" data-testid="guest-low-chips-close" onClick={onClose} aria-label="Close" className="p-1.5 rounded-full text-slate-400 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
  </div>
);

const CONFETTI = ["#f5c542", "#e2b44b", "#fff1c1", "#d4a537", "#b8860b", "#ffd970"];

// Always mounted for signed-in players: plays the welcome the moment a guest joins.
export const JoinCelebrationHost = () => {
  const [party, setParty] = useState(null);
  useEffect(() => {
    const on = (e) => setParty(e.detail);
    window.addEventListener("pfp-joined", on);
    return () => window.removeEventListener("pfp-joined", on);
  }, []);
  return party ? <JoinCelebration name={party.name} chips={party.chips} onDone={() => setParty(null)} /> : null;
};

// The welcome: gold confetti and the 50,000 chips counting into the balance.
export const JoinCelebration = ({ name, chips, onDone }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    let raf;
    const tick = (t) => { const p = Math.min(1, (t - t0) / 1800); setN(Math.round(chips * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    window.dispatchEvent(new Event("pfp-balances"));
    return () => cancelAnimationFrame(raf);
  }, [chips]);
  return (
    <div data-testid="join-celebration" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 jackpot-dim overflow-hidden" onClick={onDone}>
      {Array.from({ length: 60 }, (_, i) => (
        <span key={i} aria-hidden className="jackpot-confetti" style={{ left: `${(i * 37) % 100}%`, background: CONFETTI[i % CONFETTI.length],
          animationDuration: `${2.4 + (i % 7) * 0.35}s`, animationDelay: `${(i % 11) * 0.12}s` }} />
      ))}
      <div className="relative gold-card rounded-3xl px-10 py-9 flex flex-col items-center gap-3 text-center jackpot-burst" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] uppercase tracking-[0.35em] gold-text">Welcome to Players Room</div>
        <div className="font-display text-2xl text-amber-50" data-testid="join-celebration-name">{name}</div>
        <div className="font-display text-5xl text-amber-300 tabular-nums" data-testid="join-celebration-chips">+{n.toLocaleString()}</div>
        <div className="text-sm text-slate-300">chips added to your balance</div>
        <button type="button" data-testid="join-celebration-done" onClick={onDone} className="mt-3 btn-gold rounded-full px-6 py-2.5 text-xs font-display tracking-[0.2em] btn-lift">LET'S PLAY</button>
      </div>
    </div>
  );
};
